import { gql } from '@apollo/client';
import {
	ScenarioViewModel
} from '@/graphql/generated';
import { DEFAULT_PAGE_SIZE } from '@/lib/constants';
import { GenericAutocomplete, AutocompleteConfig } from './generic-autocomplete';
import { ScenarioCreateForm } from './scenario-create-form';

export interface ScenarioAutocompleteProps {
	value: string;
	onChange: (value: string, scenario: ScenarioViewModel | null) => void;
	placeholder?: string;
	disabled?: boolean;
	className?: string;
	enableCreateNew?: boolean;
	initialSelectedLabel?: string; // Display label to show when form loads with a selected value
}

// GraphQL query for scenarios
const SCENARIOS_QUERY = gql`
	query ScenariosList(
		$where: ScenarioViewModelFilterInput
		$first: Int
		$after: String
	) {
		scenarios(where: $where, first: $first, after: $after) {
			edges {
				node {
					scenarioId
					name
					facility {
						name
					}
				}
			}
			pageInfo {
				hasNextPage
				endCursor
			}
		}
	}
`;

// Helper functions for scenario display
export const getScenarioDisplayName = (scenario: ScenarioViewModel): string => {
	return scenario.name || 'Unnamed Scenario';
};

export const getScenarioSecondaryText = (scenario: ScenarioViewModel): string => {
	// Show facility name as secondary text
	if (scenario.facility?.name) {
		return `Facility: ${scenario.facility.name}`;
	}
	return '';
};

// Create scenario autocomplete configuration
const createScenarioAutocompleteConfig = (enableCreateNew = false): AutocompleteConfig<ScenarioViewModel> => ({
	query: SCENARIOS_QUERY,
	queryName: 'scenarios',
	searchFields: ['description'],
	getDisplayName: getScenarioDisplayName,
	getSecondaryText: getScenarioSecondaryText,
	getId: (scenario) => scenario.scenarioId,
	pageSize: DEFAULT_PAGE_SIZE,
	// Enable details functionality
	enableDetails: true,
	entityType: 'scenarios',
	createNew: enableCreateNew ? {
		enabled: true,
		entityName: 'Scenario',
		createFormComponent: ScenarioCreateForm,
	} : undefined,
});

export const ScenarioAutocomplete: React.FC<ScenarioAutocompleteProps> = ({
	value,
	onChange,
	placeholder = 'Search scenarios...',
	disabled = false,
	className = '',
	enableCreateNew = false,
	initialSelectedLabel
}) => {
	const config = createScenarioAutocompleteConfig(enableCreateNew);

	return (
		<GenericAutocomplete<ScenarioViewModel>
			config={config}
			value={value}
			onChange={onChange}
			placeholder={placeholder}
			disabled={disabled}
			className={className}
			initialSelectedLabel={initialSelectedLabel}
		/>
	);
};
